import React, { useCallback, useRef } from "react";
import { keepPreviousData as keepPreviousQueryData } from "@tanstack/react-query";
import { atom, useAtom } from "jotai";
import {
  bugsnagGeneralErrorHandler,
  recursiveCamelCaseCipher,
} from "../../postgrestApi";
import { EquipmentSummary } from "./types";
import FetchClients, { ClientOutput, ClientPaths } from "../client";
import usePreRequestFeatureAccessCheck, {
  CheckFeatureAccessHeader,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { PORTFOLIO_EQUIPMENT_QUERY_KEY } from "./queryKeys";
import {
  EQUIPMENT_SPECS_READS_HEADER_NAME,
  EquipmentRequestHeaders,
  useEquipmentSpecsReadHeaders,
} from "./equipmentSpecsHeaders";
import { downloadFileByUrl } from "@src/global_functions/util";
import { BackpackServiceError, throwBackpackResponseError } from "../errors";
import { useDownloadNotifier } from "@src/straps/derived/Notifier/DownloadNotifier";

const portfolio = {
  useQueryAccount: useQuery,
  useExportCsv: useExportPortfolioEquipmentCsv,
};

export default portfolio;

export type PortfolioEquipmentQueryParamFilters = Omit<
  NonNullable<
    ClientPaths["equipment"]["/equipment/summary"]["get"]["parameters"]["query"]
  >,
  "account_id" | "status"
>;
export type EquipmentStatusFilterValue =
  ClientOutput["equipment"]["EquipmentStatus"];
export type EquipmentAgeRangeFilterValue =
  ClientOutput["equipment"]["AgeRange"];

export type EquipmentExportStatusResponse =
  ClientOutput["equipment"]["EquipmentExportStatusResponse"];

export const portfolioEquipmentCsvExportInProgressAtom = atom(false);

const EXPORT_POLL_INTERVAL_MS = 2500;
const EXPORT_MAX_WAIT_MS = 15 * 60_000;
const EXPORT_MAX_TRANSIENT_RETRIES = 3;
// Progress bar is fake/estimated — scale from the on-screen filtered total.
// Calibrated from ~3500 rows completing in ~5s (includes create + poll overhead).
const EXPORT_PROGRESS_BASELINE_COUNT = 3500;
const EXPORT_PROGRESS_BASELINE_MS = 5_000;
// Small buffer so the bar doesn't sit at 97% before the job finishes.
const EXPORT_PROGRESS_BUFFER = 1.25;
const EXPORT_PROGRESS_MIN_MS = EXPORT_POLL_INTERVAL_MS;
const EXPORT_PROGRESS_MAX_MS = 60_000;

function getExportProgressEstimateMs(totalEquipment?: number) {
  if (!totalEquipment || totalEquipment <= 0) {
    return Math.round(EXPORT_PROGRESS_BASELINE_MS * EXPORT_PROGRESS_BUFFER);
  }

  const scaled =
    (totalEquipment / EXPORT_PROGRESS_BASELINE_COUNT) *
    EXPORT_PROGRESS_BASELINE_MS *
    EXPORT_PROGRESS_BUFFER;

  return Math.min(
    EXPORT_PROGRESS_MAX_MS,
    Math.max(EXPORT_PROGRESS_MIN_MS, Math.round(scaled))
  );
}

const EXPORT_TIMEOUT_MESSAGE =
  "Export is taking longer than expected. Please try again later.";
const EXPORT_EXPIRED_MESSAGE =
  "This export has expired. Please export again to generate a new file.";
const EXPORT_FAILED_MESSAGE = "Export failed. Please try again.";

// endpoint requires {feature: "Equipment", access: "read"}
const getPortfolioEquipmentSummary = async (
  account_id: number,
  headers: EquipmentRequestHeaders,
  filterParams?: PortfolioEquipmentQueryParamFilters
): Promise<EquipmentSummary> => {
  const { data, response, error } = await FetchClients.equipment.GET(
    "/equipment/summary",
    {
      params: {
        query: {
          account_id,
          ...filterParams,
        },
      },
      headers,
    }
  );
  // 404 is returned in the case that we are filtering by a site id and an asset manager id, wherin the asset manager does not have access to the site
  // This is a workaround to avoid the error from the query
  if (response.status === 404) {
    return {
      totalEquipment: 0,
      totalReplacementValue: 0,
      onWatchList: 0,
      linkedToProjects: 0,
      valueSummary: [],
      actionableEquipment: [],
      sites: [],
    };
  }
  if (data) {
    return recursiveCamelCaseCipher(data);
  }
  throw new Error(error);
};

function useQuery(
  accountId: number,
  filterParams: PortfolioEquipmentQueryParamFilters = {},
  keepPreviousData: boolean = false
) {
  // sorting filter params to ensure the query key is consistent
  const sortedFilterParams = React.useMemo(() => {
    return Object.entries(filterParams).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }
      return {
        ...acc,
        [key]: [...value].sort((first, second) =>
          String(first).localeCompare(String(second))
        ),
      };
    }, {} as PortfolioEquipmentQueryParamFilters);
  }, [filterParams]);

  const equipmentSpecsHeaders = useEquipmentSpecsReadHeaders();

  return useQueryWithAccessCheckLog(
    ["Equipment", "read"],
    (checkFeatureAccessHeader) =>
      getPortfolioEquipmentSummary(
        accountId,
        { ...checkFeatureAccessHeader, ...equipmentSpecsHeaders },
        sortedFilterParams
      ),
    {
      queryKey: [
        PORTFOLIO_EQUIPMENT_QUERY_KEY,
        accountId,
        sortedFilterParams,
        equipmentSpecsHeaders[EQUIPMENT_SPECS_READS_HEADER_NAME],
      ],
      ...(keepPreviousData ? { placeholderData: keepPreviousQueryData } : {}),
      staleTime: 60000,
    }
  );
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

class PortfolioExportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PortfolioExportError";
  }
}

function isRetryablePollFailure(error: unknown) {
  if (
    error instanceof PortfolioExportError ||
    error instanceof BackpackServiceError
  ) {
    return false;
  }
  if (!(error instanceof Error)) {
    return true;
  }
  const message = error.message.toLowerCase();
  return (
    message.includes("network") ||
    message.includes("fetch") ||
    message.includes("failed to fetch")
  );
}

// endpoint requires {feature: "Equipment", access: "read"}
async function createPortfolioEquipmentExport(
  accountId: number,
  filterParams: PortfolioEquipmentQueryParamFilters,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.equipment.POST(
    "/equipment/exports",
    {
      params: {
        query: {
          account_id: accountId,
          ...filterParams,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );

  // 4xx/5xx (including 429 pending-limit) use standard ErrorObject
  if (error) {
    throwBackpackResponseError(error);
  }

  if (!data?.export_id) {
    throw new PortfolioExportError(EXPORT_FAILED_MESSAGE);
  }

  return data;
}

// endpoint requires {feature: "Equipment", access: "read"}
export async function getPortfolioEquipmentExportStatus(
  exportId: string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<EquipmentExportStatusResponse> {
  const { data, error } = await FetchClients.equipment.GET(
    "/equipment/exports/{export_id}",
    {
      params: {
        path: { export_id: exportId },
      },
      headers: checkFeatureAccessHeader,
    }
  );

  // True HTTP errors (403/404/500/...) use ErrorObject — not job-outcome payloads.
  // Job outcomes (pending/ready/failed/expired) always arrive as 2xx `data`.
  if (error) {
    throwBackpackResponseError(error);
  }

  if (!data) {
    throw new PortfolioExportError(EXPORT_FAILED_MESSAGE);
  }

  return data;
}

function resolveReadyExport(status: EquipmentExportStatusResponse): {
  downloadUrl: string;
  filename?: string;
} | null {
  if (status.status === "pending") {
    return null;
  }

  if (status.status === "ready" && status.download_url) {
    return {
      downloadUrl: status.download_url,
      filename: status.filename ?? undefined,
    };
  }

  if (status.status === "failed") {
    throw new PortfolioExportError(status.error ?? EXPORT_FAILED_MESSAGE);
  }

  if (status.status === "expired") {
    throw new PortfolioExportError(status.error ?? EXPORT_EXPIRED_MESSAGE);
  }

  throw new PortfolioExportError(EXPORT_FAILED_MESSAGE);
}

export async function pollPortfolioEquipmentExport(
  exportId: string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader,
  { maxWaitMs = EXPORT_MAX_WAIT_MS }: { maxWaitMs?: number } = {}
): Promise<{ downloadUrl: string; filename?: string }> {
  const startedAt = Date.now();
  let transientFailures = 0;

  while (Date.now() - startedAt < maxWaitMs) {
    await sleep(EXPORT_POLL_INTERVAL_MS);

    try {
      const status = await getPortfolioEquipmentExportStatus(
        exportId,
        checkFeatureAccessHeader
      );
      transientFailures = 0;

      const readyExport = resolveReadyExport(status);
      if (readyExport) {
        return readyExport;
      }
    } catch (caughtError) {
      if (!isRetryablePollFailure(caughtError)) {
        throw caughtError;
      }

      transientFailures += 1;
      if (transientFailures > EXPORT_MAX_TRANSIENT_RETRIES) {
        throw new PortfolioExportError(EXPORT_FAILED_MESSAGE);
      }
    }
  }

  throw new PortfolioExportError(EXPORT_TIMEOUT_MESSAGE);
}

function useExportPortfolioEquipmentCsv(accountId: number) {
  const { queueDownload } = useDownloadNotifier();
  const { checkFeatureAccessHeader, logInsufficientAccessWarning } =
    usePreRequestFeatureAccessCheck("Equipment", "read");
  const [inProgress, setInProgress] = useAtom(
    portfolioEquipmentCsvExportInProgressAtom
  );
  // Sync guard — atom `inProgress` alone can miss same-tick double calls.
  const inProgressRef = useRef(false);

  const exportCsv = useCallback(
    async (
      filterParams: PortfolioEquipmentQueryParamFilters = {},
      options: { totalEquipment?: number } = {}
    ) => {
      if (inProgressRef.current) {
        return;
      }

      inProgressRef.current = true;
      logInsufficientAccessWarning();
      setInProgress(true);

      const prepareExport = async () => {
        try {
          const created = await createPortfolioEquipmentExport(
            accountId,
            filterParams,
            checkFeatureAccessHeader
          );
          return await pollPortfolioEquipmentExport(
            created.export_id,
            checkFeatureAccessHeader
          );
        } finally {
          inProgressRef.current = false;
          setInProgress(false);
        }
      };

      const startBrowserDownload = (downloadUrl: string) => {
        try {
          downloadFileByUrl(downloadUrl);
        } catch (error) {
          // Export already succeeded — don't surface this as an export failure.
          bugsnagGeneralErrorHandler(error);
        }
      };

      if (queueDownload) {
        // Fire-and-forget: DownloadNotifier owns progress/error UI.
        void (async () => {
          let downloadUrl: string | undefined;
          let resolveExport!: () => void;
          let rejectExport!: (error: unknown) => void;
          const exportPromise = new Promise<void>((resolve, reject) => {
            resolveExport = resolve;
            rejectExport = reject;
          });

          const queueing = queueDownload(
            {
              name: "Portfolio Equipment CSV",
              icon: "move-down",
              estimatedTimeMs: getExportProgressEstimateMs(
                options.totalEquipment
              ),
              warnOnLeave: true,
            },
            exportPromise
          );

          try {
            const result = await prepareExport();
            downloadUrl = result.downloadUrl;
            resolveExport();
          } catch (error) {
            rejectExport(error);
            await queueing;
            return;
          }

          await queueing;
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, 0);
          });
          if (downloadUrl) {
            startBrowserDownload(downloadUrl);
          }
        })();
        return;
      }

      // Fallback when DownloadNotifier isn't mounted. Call sites use void exportCsv(...),
      // so catch here to avoid an unhandled rejection (inProgress is cleared in finally).
      try {
        const result = await prepareExport();
        startBrowserDownload(result.downloadUrl);
      } catch (error) {
        bugsnagGeneralErrorHandler(error);
        alert(EXPORT_FAILED_MESSAGE);
      }
    },
    [
      accountId,
      checkFeatureAccessHeader,
      logInsufficientAccessWarning,
      queueDownload,
      setInProgress,
    ]
  );

  return { exportCsv, inProgress };
}
